/**
 * Recurring daycare series — shared client helpers (migration 0096).
 *
 * Day strings are facility-local YYYY-MM-DD; format them with a UTC timezone
 * so the browser's own zone can never shift the displayed date.
 */

export const WEEKDAY_OPTIONS: ReadonlyArray<{ value: number; label: string; long: string }> = [
  { value: 1, label: 'Mon', long: 'Monday' },
  { value: 2, label: 'Tue', long: 'Tuesday' },
  { value: 3, label: 'Wed', long: 'Wednesday' },
  { value: 4, label: 'Thu', long: 'Thursday' },
  { value: 5, label: 'Fri', long: 'Friday' },
  { value: 6, label: 'Sat', long: 'Saturday' },
  { value: 7, label: 'Sun', long: 'Sunday' },
];

export type SessionType = 'full_day' | 'half_day_am' | 'half_day_pm';

export const SESSION_OPTIONS: ReadonlyArray<{ value: SessionType; label: string }> = [
  { value: 'full_day', label: 'Full day' },
  { value: 'half_day_am', label: 'Half day (morning)' },
  { value: 'half_day_pm', label: 'Half day (afternoon)' },
];

export function sessionLabel(value: string): string {
  return SESSION_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

export function weekdaysLabel(weekdays: number[]): string {
  const labels = WEEKDAY_OPTIONS.filter((w) => weekdays.includes(w.value)).map((w) => w.label);
  if (labels.length === 7) return 'Every day';
  if (labels.length === 5 && !weekdays.includes(6) && !weekdays.includes(7)) return 'Weekdays';
  return labels.join(', ') || '—';
}

/** "Mon 6 Jul 2026" for a YYYY-MM-DD day string. */
export function formatDay(day: string, withYear = false): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return day;
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  }).format(new Date(`${day}T00:00:00Z`));
}

export function describeSkipReason(reason: string): string {
  switch (reason) {
    case 'facility_total_full':
      return 'Fully booked';
    case 'size_bucket_full':
      return 'No space for this size';
    case 'session_not_offered':
      return 'Session not offered';
    case 'vaccines_required':
      return 'Vaccinations required';
    case 'membership_past_due':
      return 'Membership payment overdue';
    case 'already_exists':
      return 'Already booked';
    default:
      return 'Unavailable';
  }
}

export interface RecurringSeriesDto {
  id: string;
  facilityId: string;
  customerId: string;
  petId: string;
  serviceId: string;
  sessionType: string;
  weekdays: number[];
  startDate: string;
  endDate: string | null;
  dropOffTime: string | null;
  pickUpTime: string | null;
  status: 'active' | 'paused' | 'ended' | string;
  materialisedThrough: string | null;
  endedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SeriesPreview {
  dates: string[];
  ok: string[];
  skipped: Array<{ date: string; reason: string }>;
  horizonWeeks: number;
  today: string;
}

export interface SeriesCreateResult {
  series: Array<{
    series: RecurringSeriesDto;
    created: Array<{ bookingId: string; date: string; totalMinor: number }>;
    skipped: Array<{ date: string; reason: string }>;
  }>;
}

export interface SeriesInstanceDto {
  bookingId: string;
  date: string;
  sessionType: string;
  status: string;
  totalMinor: number;
  burnSource?: string | null;
}

export interface SeriesRuleBody {
  petIds: string[];
  sessionType: SessionType;
  weekdays: number[];
  startDate: string;
  endDate?: string | null;
  dropOffTime?: string | null;
  pickUpTime?: string | null;
  facilityId?: string;
  serviceId?: string;
  customerId?: string;
}
